// ─────────────────────────────────────────────────────────────────────────────
// Crew — first-run consent banner (Agent B).
//
// UK-GDPR: behavioural analytics is OFF until the user opts in. This banner is the
// first-run surface; full granular control lives in Settings. Declining must be as
// easy as accepting (no dark patterns) and NEVER blocks using the product.
//
// "Essential" is always-on (not shown as a toggle here). This banner only governs
// the optional `analytics` scope; card-link + marketing stay in Settings where the
// guardrail copy can be read in full.
// ─────────────────────────────────────────────────────────────────────────────

import { Button } from './Button';
import { navigate } from '../lib/router';

interface ConsentBannerProps {
  onChoice: (granted: boolean) => void;
  busy?: boolean;
}

export function ConsentBanner({ onChoice, busy = false }: ConsentBannerProps) {
  return (
    <div
      role="region"
      aria-label="Privacy choices"
      className="fixed inset-x-0 bottom-0 z-30 border-t border-sand-200 bg-sand-50/98 backdrop-blur"
    >
      <div className="mx-auto w-full max-w-2xl px-4 py-4">
        <p className="text-sm text-ink-800">
          Crew uses essential cookies to keep you signed in. We&rsquo;d also like to use{' '}
          <span className="font-semibold">analytics</span> to understand how groups plan together so
          we can improve Crew. This is optional and you can change it any time in Settings.
        </p>
        <p className="mt-1 text-xs text-ink-700/70">
          We never sell your data. Read our{' '}
          <a
            href="#/privacy"
            onClick={(e) => {
              // #/privacy is Agent D's surface; let the native hash nav take over.
              e.preventDefault();
              navigate('settings');
              window.location.hash = '#/privacy';
            }}
            className="font-medium underline underline-offset-2 hover:text-ink-900"
          >
            privacy &amp; data rights
          </a>
          .
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button variant="primary" busy={busy} onClick={() => onChoice(true)}>
            Allow analytics
          </Button>
          <Button variant="secondary" disabled={busy} onClick={() => onChoice(false)}>
            Essential only
          </Button>
        </div>
      </div>
    </div>
  );
}
